import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, pluck, shareReplay, startWith } from 'rxjs/operators';
import { CarouselLayout, GenericCarouselLayout } from '../../../common';
import { Language } from '../../enums';
import { ClientConfigService, CoreContentData, ImgQuery, Screen } from '../../services';
import { carouselItemAnimations, getImgQueryByCarouselLayout } from '../../utils';
import { ContentViewModel } from '../carousel-item/content-view.model';

@Component({
  selector: 'osn-shared-season-carousel-item',
  templateUrl: './season-carousel-item.component.html',
  styleUrls: ['./season-carousel-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [carouselItemAnimations],
})
export class SeasonCarouselItemComponent implements OnInit {
  @Input() public hideShadow = false;
  @Input() public vm: ContentViewModel;
  @Input() public preventHover = false;
  @Input() public aspectRatio: string;
  @Input() public layout: CarouselLayout | GenericCarouselLayout;
  @Input() public carouselTitle = '';
  @Input() public showSeoTitle = false;
  @Output() public itemSelect: EventEmitter<string> = new EventEmitter();

  public lang$: Observable<Language>;
  public isArabic$: Observable<boolean>;
  public hovered = false;
  public imgQuery: ImgQuery;
  public previewImgQuery: ImgQuery;

  constructor(private readonly translateService: TranslateService, private readonly configService: ClientConfigService) {}
  public ngOnInit() {
    this.imgQuery = getImgQueryByCarouselLayout(this.layout);
    // If carouselType is portrait, add 500px static width for preloaded preview images
    // otherwise preview image and carousel thumbnail width will be the same.
    this.previewImgQuery = this.isLandscape
      ? this.imgQuery
      : Object.keys(Screen).reduce((acc, currSize) => ({ ...acc, [currSize]: 500 }), {} as ImgQuery);
    // TODO - use localizationService instead

    this.lang$ = this.translateService.onLangChange.pipe(
      pluck('lang'),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
      map((lang: Language) => lang)
    );

    this.isArabic$ = this.lang$.pipe(
      startWith(this.translateService.currentLang),
      map((lang: Language) => lang === 'ar')
    );
  }

  public getImageUrl(content: CoreContentData): string {
    if (this.layout.toLowerCase().includes('landscape')) {
      return content.landscapeImageUrl ?? 'assets/images/placeholder/contentLandscape.png';
    } else {
      return content.portraitImageUrl ?? 'assets/images/placeholder/contentPortrait.png';
    }
  }

  @HostBinding('attr.aria-label') public get ariaLabel(): string {
    return this.vm.title.en;
  }

  public get showSeasonNumber(): boolean {
    return this.configService.seasonCarouselShowSeasonNumber;
  }

  public onCarouselItemHovered(hovered: boolean) {
    this.hovered = hovered;
  }

  public onCarouselItemSelected({ carouselItemDetails }) {
    this.itemSelect.emit(carouselItemDetails.id);
  }

  public get isLandscape(): boolean {
    return [
      CarouselLayout.LandscapeLarge,
      CarouselLayout.LandscapeSmall,
      GenericCarouselLayout.Large,
      GenericCarouselLayout.Medium,
      GenericCarouselLayout.Small,
    ].includes(this.layout);
  }

  public get navigationState(): { category: string } {
    return { category: this.carouselTitle };
  }
}
